import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, Lock, ShieldCheck, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VerificationAnswersForm } from "@/components/Verification";
import { useLang } from "@/context/LanguageContext";

const STATUS = {
  pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, label: "fr.stPending" },
  rejected: { cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle, label: "fr.stRejected" },
  approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "fr.stApproved" },
};

function ReportCard({ r, onYes }) {
  const { t } = useLang();
  const st = STATUS[r.verification_status];
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid={`finder-report-${r.id}`}>
      <img src={r.photo} alt={t("fr.photoAlt")} className="w-full h-40 object-cover" data-testid={`finder-report-photo-${r.id}`} />
      <div className="p-2.5">
        {st ? (
          <p className={`text-xs font-semibold rounded-lg border px-2 py-1.5 flex items-center gap-1.5 ${st.cls}`} data-testid={`finder-report-status-${r.id}`}>
            <st.icon size={13} /> {t(st.label)}
          </p>
        ) : (
          <Button size="sm" onClick={() => onYes(r)} className="w-full rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid={`finder-report-yes-${r.id}`}>
            {t("fr.yesMine")}
          </Button>
        )}
      </div>
    </div>
  );
}

export function FinderReportsReview({ ad, reload, onDecline, declining, unlockLabel, onUnlock, unlocking }) {
  const { t } = useLang();
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const reports = ad.finder_reports || [];
  const anyOpen = reports.some((r) => r.verification_status === "none");

  async function submit(answers) {
    setBusy(true);
    try {
      await api(`/b2c/ads/${ad.id}/finder-reports/${target.id}/verify-claim`, { method: "POST", b2c: true, body: { answers } });
      toast.success(t("fr.sent"));
      setTarget(null);
      reload?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  if (ad.lost_verification_approved) {
    return (
      <div className="mt-3 border-t border-border pt-3" data-testid="finder-reports-approved">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
          <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 size={14} /> {t("fr.approvedTitle")}</p>
          <p className="text-xs text-emerald-700 mt-1">{t("fr.approvedHint")}</p>
          <Button size="sm" onClick={onUnlock} disabled={unlocking} className="mt-2.5 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="finder-reports-unlock-btn">
            {unlocking ? "…" : unlockLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="finder-reports-review">
      <div className="rounded-xl bg-findo-navy/[0.04] border border-findo-navy/15 p-3">
        <p className="text-sm font-semibold text-findo-navy flex items-center gap-1.5"><Lock size={14} /> {t("fr.isItYours")}</p>
        <p className="text-xs text-slate-500 mt-1">{t("fr.isItYoursHint")}</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {reports.map((r) => <ReportCard key={r.id} r={r} onYes={setTarget} />)}
        </div>
        {anyOpen && (
          <Button size="sm" variant="outline" onClick={onDecline} disabled={declining} className="mt-3 w-full rounded-full font-semibold text-slate-600" data-testid="finder-reports-no-btn">
            {declining ? "…" : t("fr.notMine")}
          </Button>
        )}
      </div>
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="finder-report-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-findo-navy flex items-center gap-2"><ShieldCheck size={20} /> {t("fr.proveTitle")}</DialogTitle>
          </DialogHeader>
          {target && <img src={target.photo} alt="" className="w-full h-44 object-cover rounded-xl" />}
          <p className="text-sm text-slate-500">{t("fr.proveHint")}</p>
          <VerificationAnswersForm questions={[]} busy={busy} onSubmit={submit} onCancel={() => setTarget(null)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
